import { AlertCircle, Camera, CheckCircle, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { QrReader } from "react-qr-reader";
import { useNavigate } from "react-router-dom";
import { activiteService } from "../services/api";

const QRScanner = () => {
  const navigate = useNavigate();
  const [selectedActivite, setSelectedActivite] = useState("");
  const [activites, setActivites] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [commentaire, setCommentaire] = useState("");
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualCode, setManualCode] = useState("");

  useEffect(() => {
    fetchActivites();
  }, []);

  const fetchActivites = async () => {
    try {
      const response = await activiteService.getActivites({ actif_only: true });
      setActivites(response.data);
      if (response.data.length > 0) {
        setSelectedActivite(response.data[0].id.toString());
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error("Erreur lors du chargement des activités");
    }
  };

  const handleScan = async (data) => {
    if (data) {
      // Rediriger vers la page de validation avec le QR code
      navigate(`/validate?qr_code=${data}`);
    }
  };

  const handleError = (err) => {
    console.error("QR Scanner error:", err);
    setCameraError("Erreur d'accès à la caméra. Vérifiez les permissions.");
    toast.error("Impossible d'accéder à la caméra");
  };

  const handleStartScan = () => {
    setCameraError(null);
    setResult(null);
    setScanning(true);
  };

  const handleManualValidation = async () => {
    if (!manualCode) {
      toast.error("Veuillez saisir un code unique");
      return;
    }

    // Pour la validation manuelle, on a besoin de récupérer le QR code du parent
    // On va d'abord chercher le parent par son code unique
    try {
      setLoading(true);
      // Ici on pourrait faire un appel API pour récupérer le QR code du parent
      // Pour l'instant, on va rediriger vers la validation avec le code unique
      // et la page de validation gérera la recherche du parent
      navigate(`/validate?code_unique=${manualCode.toUpperCase()}`);
    } catch (error) {
      console.error("Error finding parent by code:", error);
      toast.error("Code unique non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const selectedActiviteData = activites.find(
    (a) => a.id.toString() === selectedActivite
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Scanner QR Code
        </h1>
        <p className="text-gray-600">
          Scannez le QR code d'un parent pour valider sa participation à une
          activité
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Instructions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-600">1</span>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">
                Sélectionner l'activité
              </h3>
              <p className="text-sm text-gray-600">
                Choisissez l'activité pour laquelle vous voulez valider les
                participations
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-600">2</span>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Scanner le QR code</h3>
              <p className="text-sm text-gray-600">
                Demandez au parent de présenter son QR code devant la caméra
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-600">3</span>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">
                Validation automatique
              </h3>
              <p className="text-sm text-gray-600">
                Le système attribue automatiquement les points et met à jour la
                carte tampons
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <div className="space-y-6">
          {/* Sélection d'activité */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Configuration
            </h2>

            {/* Mode de validation */}
            <div className="mb-4">
              <label className="label">Mode de validation</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setManualMode(false);
                    setScanning(false);
                    setResult(null);
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    !manualMode
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  📱 Scanner QR
                </button>
                <button
                  onClick={() => {
                    setManualMode(true);
                    setScanning(false);
                    setResult(null);
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    manualMode
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  ⌨️ Code manuel
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="label">Activité</label>
              <select
                value={selectedActivite}
                onChange={(e) => setSelectedActivite(e.target.value)}
                className="input-field"
                disabled={scanning}
              >
                {activites.map((activite) => (
                  <option key={activite.id} value={activite.id}>
                    {activite.nom} - {activite.type_activite}
                  </option>
                ))}
              </select>
            </div>

            {/* Saisie manuelle du code */}
            {manualMode && (
              <div className="mb-4">
                <label className="label">Code unique du parent</label>
                <input
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  placeholder="Ex: ABC123"
                  className="input-field"
                  maxLength={6}
                  style={{ textTransform: "uppercase" }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Saisissez le code unique à 6 caractères du parent
                </p>
              </div>
            )}

            {selectedActiviteData && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">
                  Détails de l'activité
                </h3>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="font-medium">Nom:</span>{" "}
                    {selectedActiviteData.nom}
                  </p>
                  <p>
                    <span className="font-medium">Type:</span>{" "}
                    {selectedActiviteData.type_activite}
                  </p>
                  <p>
                    <span className="font-medium">Points:</span>{" "}
                    {selectedActiviteData.points_attribues}
                  </p>
                  {selectedActiviteData.date_activite && (
                    <p>
                      <span className="font-medium">Date:</span>{" "}
                      {new Date(
                        selectedActiviteData.date_activite
                      ).toLocaleDateString()}
                    </p>
                  )}
                  {selectedActiviteData.lieu && (
                    <p>
                      <span className="font-medium">Lieu:</span>{" "}
                      {selectedActiviteData.lieu}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className="label">Commentaire (optionnel)</label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                className="input-field"
                rows={3}
                placeholder="Ajouter un commentaire..."
                disabled={scanning}
              />
            </div>
          </div>

          {/* Résultat du scan */}
          {result && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Résultat
              </h2>

              <div
                className={`p-4 rounded-lg ${
                  result.success
                    ? "bg-success-50 border border-success-200"
                    : "bg-red-50 border border-red-200"
                }`}
              >
                <div className="flex items-center">
                  {result.success ? (
                    <CheckCircle className="h-6 w-6 text-success-600 mr-3" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600 mr-3" />
                  )}
                  <div>
                    <p
                      className={`font-medium ${
                        result.success ? "text-success-800" : "text-red-800"
                      }`}
                    >
                      {result.message}
                    </p>
                    {result.success && result.data && (
                      <div className="mt-2 text-sm text-success-700">
                        <p>
                          Parent:{" "}
                          {result.data.parent?.user?.nom ||
                            result.data.parent?.nom ||
                            "N/A"}
                        </p>
                        <p>Enfant: {result.data.parent?.nom_enfant || "N/A"}</p>
                        <p>
                          Points attribués: +{result.data.points_obtenus || 0}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scanner / Validation manuelle */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {manualMode ? "Validation manuelle" : "Scanner QR Code"}
          </h2>

          {!selectedActivite ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                Veuillez sélectionner une activité avant de valider
              </p>
            </div>
          ) : manualMode ? (
            /* Mode validation manuelle */
            <div className="space-y-4">
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⌨️</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Validation par code unique
                </h3>
                <p className="text-gray-600 mb-6">
                  Demandez au parent de vous donner son code unique à 6
                  caractères
                </p>

                <div className="max-w-xs mx-auto">
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) =>
                      setManualCode(e.target.value.toUpperCase())
                    }
                    placeholder="Ex: ABC123"
                    className="w-full px-4 py-3 text-center text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={6}
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
              </div>

              <button
                onClick={handleManualValidation}
                disabled={loading || !manualCode}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all duration-200"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Validation en cours...
                  </>
                ) : (
                  <>
                    <span className="mr-2">✓</span>
                    Valider la participation
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                {scanning ? (
                  <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Scan en cours...</p>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <div className="text-center">
                      <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">
                        Positionnez le QR code dans le cadre
                      </p>
                      <button
                        onClick={handleStartScan}
                        className="btn-primary mt-4"
                      >
                        Démarrer le scan
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {scanning && (
                <div className="space-y-4">
                  {cameraError ? (
                    <div className="aspect-square bg-red-50 rounded-lg border-2 border-red-200 flex items-center justify-center">
                      <div className="text-center p-6">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-red-800 mb-2">
                          Erreur de caméra
                        </h3>
                        <p className="text-red-600 mb-4">{cameraError}</p>
                        <button
                          onClick={handleStartScan}
                          className="btn-primary"
                        >
                          Réessayer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative">
                      <QrReader
                        delay={300}
                        onError={handleError}
                        onScan={handleScan}
                        style={{ width: "100%", height: "100%" }}
                        constraints={{
                          facingMode: "environment",
                          video: {
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                          },
                        }}
                        videoStyle={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {/* Overlay pour indiquer la zone de scan */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-white rounded-lg shadow-lg">
                          <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-blue-500 rounded-tl-lg"></div>
                          <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-blue-500 rounded-tr-lg"></div>
                          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-blue-500 rounded-bl-lg"></div>
                          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-blue-500 rounded-br-lg"></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        setScanning(false);
                        setCameraError(null);
                      }}
                      className="btn-secondary flex-1"
                    >
                      Arrêter le scan
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
